/**
 * Where a path nothing answers lands. The Worker serves the app for every unknown path, so this
 * page is the only place a mistyped or retired address is told so. It asks search engines not to
 * index it, because the response status is 200.
 */
import * as stylex from "@stylexjs/stylex";
import type { ReactNode } from "react";
import { useEffect, useRef } from "react";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/space.stylex";
import { radius, space } from "@lattice/tokens/space.stylex";
import "@lattice/tokens/text.stylex";
import { text } from "@lattice/tokens/text.stylex";
import { focusRing } from "@lattice/ui/focus";
import { LinkButton } from "@lattice/ui/LinkButton";
import { PublicPage } from "./PublicPage.tsx";
const styles = {
  heading: {
    kogj98: "x1ghz6dp",
    kGuDYH: "x1u29x3t",
    k63SB2: "xqu410z",
    kQqvRs: "x1ntj4s9",
    kb6lSQ: "x1d3j7mx",
    kLWn49: "x1e5flnj",
    kN2L0X: "x1w2vvpw",
    $$css: true
  },
  placeLink: {
    k1xSpc: "x1lliihq",
    kMwMTN: "xzb3lse",
    k63SB2: "xqu410z",
    kybGjl: "x1bvjpef x1lku1pv",
    kcSHmL: "xda060z",
    kaIpWk: "x8wwnw1",
    $$css: true
  },
  focusTarget: {
    k3XXqK: "x1t137rt",
    $$css: true
  }
};

/** Keeps the page out of search results for as long as it is mounted. */
export function useNoIndex(): void {
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex";
    document.head.append(meta);
    return () => meta.remove();
  }, []);
}

/** The page heading, focused on arrival so a screen reader announces where the visitor landed. */
export function FocusedTitle({
  styled,
  children
}: {
  readonly styled: readonly stylex.StyleXStyles[];
  readonly children: ReactNode;
}) {
  const ref = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    ref.current?.focus();
  }, []);
  return <h1 ref={ref} tabIndex={-1} {...stylex.props(...styled, styles.focusTarget)}>
      {children}
    </h1>;
}
const ADDRESS_MAX = 120;

/** The address the visitor asked for, as the path alone, so a long or odd one cannot fill the page. */
export function AskedAddress() {
  const path = window.location.pathname;
  const shown = path.length > ADDRESS_MAX ? `${path.slice(0, ADDRESS_MAX)}…` : path;
  return <div className="x78zum5 xdt5ytf xskm3c9">
      <p className="x1ghz6dp xl4dl4e x1jty25b">You asked for</p>
      <p className="x1ghz6dp xzb3lse xj0a0fe">{shown}</p>
    </div>;
}
export interface Place {
  readonly href: string;
  readonly name: string;
  readonly hint: string;
}

/** Links to where the visitor most likely meant to go, each with a line saying what is there. */
export function Places({
  label,
  places
}: {
  readonly label: string;
  readonly places: readonly Place[];
}) {
  return <nav aria-label={label} className="x78zum5 xdt5ytf xskm3c9">
      <p aria-hidden="true" className="x1ghz6dp xl4dl4e x1jty25b">
        {label}
      </p>
      <ul className="x1ghz6dp x1717udv xe8uvvx x78zum5 xdt5ytf x1173whf">
        {places.map(place => <li key={place.href}>
            <a href={place.href} {...stylex.props(styles.placeLink, focusRing)}>
              {place.name}
            </a>
            <span className="x1lliihq x1jty25b">{place.hint}</span>
          </li>)}
      </ul>
    </nav>;
}
const SITE_PLACES: readonly Place[] = [{
  href: "/#how",
  name: "How it works",
  hint: "From your labs to a signed formula"
}, {
  href: "/#pricing",
  name: "Pricing",
  hint: "$175 a month, charged after a physician signs"
}, {
  href: "/start",
  name: "Upload your labs",
  hint: "Start with the results you have"
}];
export function NotFound() {
  useNoIndex();
  return <PublicPage title="Page not found · Lattice">
      <div className="x78zum5 xdt5ytf x15agyyk">
        <FocusedTitle styled={[styles.heading]}>We can't find that page</FocusedTitle>
        <p className="x1ghz6dp x1jty25b x1fzhlzt x1hw683e">
          There is no page at this address. If a link from us brought you here, contact support and
          we will fix it.
        </p>
        <div className="x78zum5 x1a02dak x626il7">
          <LinkButton href="/">Back to the home page</LinkButton>
          <LinkButton href="/support" tone="quiet">
            Contact support
          </LinkButton>
        </div>
        <AskedAddress />
        <Places label="Places you may have meant" places={SITE_PLACES} />
      </div>
    </PublicPage>;
}